import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useOutlet } from "@/contexts/OutletContext";
import { fmt } from "@/lib/format";
import { TrendUp, TrendDown, Coins, Receipt, Wallet, Percent, Package, Motorcycle } from "@phosphor-icons/react";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
  { key: "mtd", label: "MTD" },
];

function Stat({ label, value, icon: Icon, accent = "bg-stone-100 text-stone-600", hint }) {
  return (
    <div className="bg-white border border-boba-stoneLine rounded-md p-5">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-boba-inkSoft font-mono">{label}</div>
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${accent}`}>
          <Icon size={18} weight="duotone" />
        </div>
      </div>
      <div className="font-mono text-2xl font-bold mt-3 text-boba-ink">{value}</div>
      {hint && <div className="text-[11px] text-boba-inkSoft mt-1">{hint}</div>}
    </div>
  );
}

export default function PnL() {
  const { outlets } = useOutlet();
  const [period, setPeriod] = useState("30d");
  const [outletFilter, setOutletFilter] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (outletFilter && outletFilter !== "all") params.set("outlet_id", outletFilter);
      const { data } = await api.get(`/pnl?${params.toString()}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [period, outletFilter]);

  const t = data?.totals;

  return (
    <div className="h-screen flex flex-col">
      <div className="p-6 bg-white border-b border-boba-stoneLine">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-bold" data-testid="pnl-title">Profit &amp; Loss</h1>
            <p className="text-sm text-boba-inkSoft mt-1">
              Revenue − COGS − Expenses − Salary − Tax − Commission = Net Profit · {data?.range?.label}
              {data?.range?.days ? ` · ${data.range.days} days` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-1 bg-boba-cream rounded-md p-1 border border-boba-stoneLine">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setPeriod(r.key)}
                  className={`px-3 h-8 rounded text-xs font-mono uppercase tracking-wider ${period === r.key ? "bg-white text-boba-ink shadow-sm" : "text-boba-inkSoft"}`}
                  data-testid={`pnl-range-${r.key}`}
                >{r.label}</button>
              ))}
            </div>
            <select
              value={outletFilter}
              onChange={(e) => setOutletFilter(e.target.value)}
              className="h-10 px-3 border border-boba-stoneLine rounded-md bg-white text-sm"
              data-testid="pnl-outlet-filter"
            >
              <option value="all">All outlets</option>
              {outlets.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {loading && !data && <div className="text-center text-stone-500 py-12">Loading P&L…</div>}
        {t && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Revenue" value={fmt(t.revenue)} icon={Coins} accent="bg-emerald-50 text-emerald-700" hint={`${t.orders} orders`} />
              <Stat label="COGS" value={fmt(t.cogs)} icon={Package} accent="bg-orange-50 text-orange-700" hint="Cost of goods sold" />
              <Stat label="Expenses" value={fmt(t.expenses)} icon={Receipt} accent="bg-rose-50 text-rose-700" />
              <Stat
                label="Salary (allocated)"
                value={fmt(t.salary)}
                icon={Wallet}
                accent="bg-indigo-50 text-indigo-700"
                hint={`Prorated over ${data.range.days} days`}
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Gross Profit" value={fmt(t.gross_profit)} icon={TrendUp} accent="bg-emerald-50 text-emerald-700" hint="Revenue − COGS" />
              <Stat label="Tax Collected" value={fmt(t.tax)} icon={Percent} accent="bg-amber-50 text-amber-700" hint="Deducted from profit" />
              <Stat label="Aggregator Commission" value={fmt(t.commission || 0)} icon={Motorcycle} accent="bg-orange-50 text-[#B85A0F]" hint="Swiggy + Zomato" />
              <div className={`border rounded-md p-5 ${t.net_profit >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`} data-testid="pnl-net-card">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-widest text-boba-inkSoft font-mono">Net Profit</div>
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center ${t.net_profit >= 0 ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"}`}>
                    {t.net_profit >= 0 ? <TrendUp size={18} weight="duotone" /> : <TrendDown size={18} weight="duotone" />}
                  </div>
                </div>
                <div className={`font-mono text-2xl font-black mt-3 ${t.net_profit >= 0 ? "text-emerald-800" : "text-rose-800"}`} data-testid="pnl-net-value">
                  {fmt(t.net_profit)}
                </div>
                <div className="text-[11px] text-boba-inkSoft mt-1">
                  Margin: {t.revenue > 0 ? ((t.net_profit / t.revenue) * 100).toFixed(1) : "0.0"}%
                </div>
              </div>
            </div>

            {/* Revenue by source */}
            {data.by_source && data.by_source.length > 0 && (
              <div className="bg-white border border-boba-stoneLine rounded-md overflow-hidden" data-testid="pnl-by-source">
                <div className="px-5 py-3 bg-boba-cream border-b border-boba-stoneLine font-display text-lg font-bold">
                  Revenue by Source
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3">
                  {data.by_source.map((s) => {
                    const styles = s.source === "swiggy"
                      ? { bar: "bg-[#FC8019]", label: "Swiggy" }
                      : s.source === "zomato"
                        ? { bar: "bg-[#E23744]", label: "Zomato" }
                        : { bar: "bg-boba-matcha", label: "In-store" };
                    const share = t.revenue > 0 ? Math.round((s.revenue / t.revenue) * 100) : 0;
                    return (
                      <div key={s.source} className="p-5 border-b md:border-b-0 md:border-r border-boba-stoneLine last:border-r-0">
                        <div className="flex items-center justify-between">
                          <div className="text-[11px] uppercase tracking-widest text-boba-inkSoft font-mono">{styles.label}</div>
                          <div className="text-[11px] text-boba-inkSoft font-mono">{s.orders} orders</div>
                        </div>
                        <div className="font-mono text-2xl font-bold mt-2">{fmt(s.revenue)}</div>
                        <div className="text-xs text-boba-inkSoft font-mono mt-1">Commission: {fmt(s.commission)} · {share}% share</div>
                        <div className="mt-2 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                          <div className={`h-full ${styles.bar}`} style={{ width: `${share}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Per outlet breakdown */}
            <div className="bg-white border border-boba-stoneLine rounded-md overflow-hidden">
              <div className="px-5 py-3 bg-boba-cream border-b border-boba-stoneLine font-display text-lg font-bold">
                By Outlet
              </div>
              {data.by_outlet.length === 0 && (
                <div className="p-6 text-center text-stone-500">No outlet activity in this range.</div>
              )}
              {data.by_outlet.length > 0 && (
                <table className="w-full text-sm" data-testid="pnl-outlets-table">
                  <thead className="text-boba-inkSoft text-[11px] uppercase tracking-widest font-mono">
                    <tr>
                      <th className="px-5 py-2 text-left">Outlet</th>
                      <th className="px-5 py-2 text-right">Orders</th>
                      <th className="px-5 py-2 text-right">Revenue</th>
                      <th className="px-5 py-2 text-right">COGS</th>
                      <th className="px-5 py-2 text-right">Expenses</th>
                      <th className="px-5 py-2 text-right">Salary</th>
                      <th className="px-5 py-2 text-right">Tax</th>
                      <th className="px-5 py-2 text-right">Commission</th>
                      <th className="px-5 py-2 text-right">Net Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_outlet.map((o) => (
                      <tr key={o.outlet_id} className="border-t border-boba-stoneLine hover:bg-stone-50" data-testid={`pnl-outlet-row-${o.outlet_id}`}>
                        <td className="px-5 py-3 font-medium">{o.outlet_name}</td>
                        <td className="px-5 py-3 text-right font-mono">{o.orders}</td>
                        <td className="px-5 py-3 text-right font-mono">{fmt(o.revenue)}</td>
                        <td className="px-5 py-3 text-right font-mono text-orange-700">{fmt(o.cogs)}</td>
                        <td className="px-5 py-3 text-right font-mono text-rose-700">{fmt(o.expenses)}</td>
                        <td className="px-5 py-3 text-right font-mono text-indigo-700">{fmt(o.salary)}</td>
                        <td className="px-5 py-3 text-right font-mono text-amber-700">{fmt(o.tax)}</td>
                        <td className="px-5 py-3 text-right font-mono text-[#B85A0F]">{fmt(o.commission || 0)}</td>
                        <td className={`px-5 py-3 text-right font-mono font-bold ${o.net_profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmt(o.net_profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border border-boba-stoneLine rounded-md">
                <div className="px-5 py-3 bg-boba-cream border-b border-boba-stoneLine font-display text-lg font-bold">
                  Expenses by Category
                </div>
                {data.expenses_by_category.length === 0 && (
                  <div className="p-6 text-center text-stone-500 text-sm">No expenses in this range.</div>
                )}
                {data.expenses_by_category.length > 0 && (
                  <table className="w-full text-sm">
                    <tbody>
                      {data.expenses_by_category.map((e) => (
                        <tr key={e.category} className="border-t border-boba-stoneLine">
                          <td className="px-5 py-2">{e.category}</td>
                          <td className="px-5 py-2 text-right font-mono font-semibold">{fmt(e.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="bg-white border border-boba-stoneLine rounded-md">
                <div className="px-5 py-3 bg-boba-cream border-b border-boba-stoneLine font-display text-lg font-bold">
                  Salary Allocation
                </div>
                {data.salary_breakdown.length === 0 && (
                  <div className="p-6 text-center text-stone-500 text-sm">No salaries configured for this outlet filter.</div>
                )}
                {data.salary_breakdown.length > 0 && (
                  <table className="w-full text-sm">
                    <thead className="text-boba-inkSoft text-[11px] uppercase tracking-widest font-mono">
                      <tr>
                        <th className="px-5 py-2 text-left">Staff</th>
                        <th className="px-5 py-2 text-left">Outlet</th>
                        <th className="px-5 py-2 text-right">Monthly</th>
                        <th className="px-5 py-2 text-right">Allocated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.salary_breakdown.map((s) => (
                        <tr key={s.id} className="border-t border-boba-stoneLine">
                          <td className="px-5 py-2">{s.staff_name} <span className="text-boba-inkSoft text-xs capitalize">({s.role?.replace("_", " ")})</span></td>
                          <td className="px-5 py-2 text-boba-inkSoft">{s.outlet_name}</td>
                          <td className="px-5 py-2 text-right font-mono">{fmt(s.monthly_salary)}</td>
                          <td className="px-5 py-2 text-right font-mono font-semibold">{fmt(s.allocated)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
